/**
 * The model catalog and run manifests, from our own API.
 *
 * Everything a model field *is* — units, decimals, the plotted range, the
 * floor, the colours — comes from here rather than from
 * `map/layers/registry.ts`. The backend registry rendered the pixels, so it is
 * the single source of truth for what they mean; a second table in the
 * frontend is the bug `GROUP_LABELS` already taught us about.
 *
 * The catalog is cached as a shared promise, the same shape as
 * `timeSource.ts`'s capabilities cache and `legendSource.ts`'s legend cache:
 * concurrent callers fetch once, and a failure is not remembered. Unlike
 * those, it also *expires*: what it describes is a worker on this same box
 * publishing a forecast step every few minutes, so a promise held for the life
 * of the page is a page that never notices a run landing.
 */

export interface ModelVariable {
  id: string
  label: string
  units: string
  decimals: number
  plotted: [number, number]
  /** The first lead, in minutes, at which this field exists. Not zero for an
   *  accumulation: every model writes lead 0 as an all-zero `0-0 day acc`
   *  field and GFS omits it. */
  min_lead: number
  /** The span the value covers, or 0 for an instantaneous field.
   *
   *  Non-zero means the field is **not stridable**: skipping three hours of
   *  four in an hourly rainfall loop understates the storm, and nothing on
   *  screen would say so. See `pickForecastSteps`. */
  window_minutes: number
  /** The name of the combiner that produced this field (`magnitude`,
   *  `difference`), or null for one record taken as it came. */
  combine: string | null
  /** Below this the renderer drew nothing and the readout says "no echo". */
  floor: number | null
  /** An ordinary number that means "nothing here". Never printed. */
  sentinel: number | null
  /** The name of the unit conversion applied at ingest, or null. */
  convert: string | null
  colormap: [number, string][]
  /**
   * Which vector artifacts sit beside this field's plates — the deployment's
   * answer, not the registry's, so a kind nobody writes is absent here and the
   * frontend row hides itself.
   */
  vectors?: VectorKind[]
  /** How to *label* the isolines; null when the field is not contoured. */
  contour?: ContourInfo | null
  /** How to label the barbs; null when the field has no components. */
  barb?: BarbInfo | null
}

/** The vector artifacts a model field can be drawn as, beside its plate. */
export type VectorKind = 'contour' | 'barb'

export interface ContourInfo {
  /** The isoline interval, in `units`. */
  interval: number
  /** The variable's own units — `hPa`, `m`. Restated here so the legend can
   *  read one block rather than two. */
  units: string
  /** Every Nth level drawn heavier, counted from zero; 0 means none. */
  emphasis: number
}

export interface BarbInfo {
  /** Always `kt`: a barb is a knots convention whatever the plate says. The
   *  backend states it rather than letting the browser assume it. */
  units: string
  /** The lattice spacing on the ground, for the legend's own sentence. */
  spacing_km: number
}

export interface ModelRun {
  /** `20260918T12Z`; the run's URL segment and directory name. */
  run: string
  complete: boolean
  expected_steps: number
  steps: number
  /** How far this run animates without a gap, in lead minutes. "has 7 steps"
   *  and "runs to +6 h" are different claims and only the second one
   *  animates. */
  contiguous_lead: number | null
  updated: string | null
}

/** A lon/lat box a model's stored grid was cropped to, or null. Outside it
 *  `/api/point` has no value at all, which is not the same as "no echo". */
export interface ModelCrop {
  west: number
  south: number
  east: number
  north: number
}

/**
 * The run this deployment is waiting on next, and when it should have it.
 *
 * The catalog's other half — `latest` and `runs` — describes what is on disk,
 * which says nothing about whether anything is coming: a worker that died an
 * hour ago and one that is forty minutes into the next cycle both read as
 * "the 20Z run, 2 h old".
 */
export interface NextRun {
  /** `20260919T21Z`. Its *progress*, once any has landed, is the matching
   *  entry in `runs` — the catalog does not send the step counts twice. */
  run: string
  /** When this deployment expects to hold the whole run, ISO. */
  expected: string
  /** Where `expected` came from. `measured` is the median of what the runs
   *  retained here actually took; `latency` is NOAA's published figure for the
   *  whole cycle, which is all there is before anything has finished. They are
   *  different claims — a deployment ingesting +48 h of a sixteen-day GFS run
   *  finishes hours before the cycle does — so the panel says which it has. */
  basis: 'measured' | 'latency'
}

export interface ModelInfo {
  id: string
  label: string
  attribution: string
  widths: number[]
  cadence_hours: number
  crop: ModelCrop | null
  variables: ModelVariable[]
  runs: ModelRun[]
  /** The newest *servable* run, not the newest directory. null when none is. */
  latest: string | null
  /** The cycle still to come, or null when nothing is expected. */
  next_run: NextRun | null
}

export interface ManifestStep {
  /** Lead time in minutes from the run. `f012` is 720; a sub-hourly step is
   *  75. The canonical key everywhere in Phase 4, because no model's axis is
   *  `range(hours)`: GFS goes 3-hourly after f120 and NBM starts at f001. */
  lead: number
  valid: string
  stats: {
    min: number | null
    max: number | null
    covered: number
    above: Record<string, number>
  }
}

/**
 * How a run's data texture decodes: the range it was **quantised with**.
 *
 * `value = lo + index / (levels - 1) * (hi - lo)`, and `index === nodata`
 * means nothing there.
 *
 * It lives on the run rather than in the catalog, and that is not tidiness. A
 * paletted plate carries its own colours, so editing a variable's `plotted`
 * range left every stored run drawing the *old* ramp until retention cycled
 * it — visibly stale, never wrong. A grayscale plate carries none, so applying
 * today's catalog range to a plate quantised against yesterday's draws **wrong
 * numbers in right-looking colours**. A run is decoded with what it was born
 * with.
 */
export interface TextureDecodeBlock {
  lo: number
  hi: number
  levels: number
  nodata: number
}

/** Which encoding of a step's pixels a run holds. */
export type PlateKind = 'palette' | 'texture'

export interface RunManifest {
  model: string
  run: string
  cycle_extended: boolean
  expected_steps: number
  complete: boolean
  updated: string
  /**
   * The plate kinds this run's steps were actually rendered as.
   *
   * The frontend picks the layer kind it can draw from **here**, never from
   * what the deployment says it writes today: a run ingested before the
   * migration has plates and no textures, and the manifest is what says so.
   * Absent on a manifest written before Phase 7, which means `['palette']`.
   */
  plates?: PlateKind[]
  grid: {
    bbox3857: [number, number, number, number]
    /** MapLibre's order: top-left, top-right, bottom-right, bottom-left. */
    coordinates: [[number, number], [number, number], [number, number], [number, number]]
    widths: number[]
    /** The grid the run was *stored* on, which with a crop is not the one the
     *  registry describes. Only `/api/point` needs it; nothing here does. */
    crs_proj4?: string
    width?: number
    height?: number
  }
  variables: Record<
    string,
    {
      declared: number[]
      steps: ManifestStep[]
      /** Which vector artifacts this run actually holds for the field. A run
       *  ingested before the field grew a contour spec has plates and no
       *  contours, and the manifest is what says so. */
      vectors?: VectorKind[]
      /** How this field's data texture decodes, for this run. Absent when the
       *  run holds no texture for it. */
      texture?: TextureDecodeBlock
    }
  >
}

/** One model's disk use, from `/api/storage`. Advisory: nothing deletes on it. */
export interface ModelStorage {
  id: string
  label: string
  bytes: number
  runs: { run: string; bytes: number; complete: boolean }[]
}

export interface StorageReport {
  budget_bytes: number
  total_bytes: number
  over_budget: boolean
  models: ModelStorage[]
}

let catalog: Promise<ModelInfo[]> | null = null
let fetchedAt = 0
/** Which `load()` owns the cache, so a slow failure cannot evict a newer answer. */
let generation = 0

/**
 * How long one catalog answer stays good.
 *
 * It used to be the whole page load, which froze every model layer, the picker
 * and the status panel on whatever the worker happened to have ingested the
 * moment the tab opened — a cold start or a run boundary went unnoticed until
 * somebody reloaded. `/api/models` is a local-disk read behind a 5 s server TTL
 * (`app/api/models.py`), so asking again costs nothing worth saving.
 *
 * This is only the floor. What actually keeps the page live is the poller in
 * `state/models.svelte.ts`; this stops a layer created between polls from being
 * built out of an hour-old catalog.
 */
const CATALOG_TTL_MS = 60_000

function load(): Promise<ModelInfo[]> {
  const mine = ++generation
  const pending = fetch('/api/models')
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`/api/models ${r.status}`))))
    .then((body: { models?: ModelInfo[] }) => body?.models ?? [])
    .catch((e) => {
      console.warn('model catalog unavailable; no model layers', e)
      // Only if nothing newer has taken the cache: a failure must not throw
      // away a good answer that a later call is already holding.
      if (mine === generation) {
        catalog = null
        fetchedAt = 0
      }
      return []
    })
  catalog = pending
  fetchedAt = Date.now()
  return pending
}

/**
 * The catalog, shared between callers and re-asked at most once a minute.
 *
 * Concurrent callers still fetch once, and a failure is still not remembered:
 * no model data is a degraded state this app already knows how to be in — the
 * layer draws nothing, the panel says so — and a worker that was restarting
 * must not cost the page its model layer for the rest of the session.
 */
export function fetchModels(): Promise<ModelInfo[]> {
  if (catalog && Date.now() - fetchedAt < CATALOG_TTL_MS) return catalog
  return load()
}

/** Ask now, whatever the TTL says. The poller's call, and nothing else's. */
export function refreshModels(): Promise<ModelInfo[]> {
  return load()
}

export async function findModel(modelId: string): Promise<ModelInfo | null> {
  return (await fetchModels()).find((m) => m.id === modelId) ?? null
}

export async function findVariable(
  modelId: string,
  varId: string,
): Promise<{ model: ModelInfo; variable: ModelVariable } | null> {
  const model = await findModel(modelId)
  const variable = model?.variables.find((v) => v.id === varId)
  return model && variable ? { model, variable } : null
}

/**
 * One run's manifest. Not cached: it *grows* as forecast hours land, and a
 * cached one presented as current would pin a stale frame list. The service
 * worker leaves these alone for the same reason.
 */
export async function fetchManifest(modelId: string, run: string): Promise<RunManifest | null> {
  try {
    const resp = await fetch(`/api/models/${encodeURIComponent(modelId)}/${encodeURIComponent(run)}`)
    if (!resp.ok) return null
    return (await resp.json()) as RunManifest
  } catch {
    return null
  }
}

export interface PointStep {
  lead: number
  valid: string
  value: number | null
  /** Present and true only for "no echo": a value below the floor or at the
   *  sentinel. Absent when the point is outside the grid, which means
   *  something else entirely. */
  below_floor?: boolean
}

export interface PointSeries {
  model: string
  run: string
  variable: string
  units: string
  decimals: number
  /** null means the point is outside this model's stored grid. With `cropped`
   *  true that is a much larger region, and the honest sentence is "outside
   *  this model's area" rather than "—". */
  grid: { row: number; col: number } | null
  cropped: boolean
  steps: PointStep[]
}

export async function fetchPointSeries(
  modelId: string,
  varId: string,
  lat: number,
  lon: number,
  options: { run?: string; lead?: number } = {},
): Promise<PointSeries | null> {
  const params = new URLSearchParams({
    model: modelId,
    var: varId,
    lat: String(lat),
    lon: String(lon),
  })
  if (options.run) params.set('run', options.run)
  if (options.lead !== undefined) params.set('lead', String(options.lead))
  try {
    const resp = await fetch(`/api/point?${params}`)
    if (!resp.ok) return null
    return (await resp.json()) as PointSeries
  } catch {
    return null
  }
}

/** The immutable plate URL. Every segment here is validated server-side.
 *
 *  `m00120.png`, not `f002.png`: one naming scheme rather than two, and NBM's
 *  15,840-minute lead fits in five digits with room. The plate *kind* is a
 *  path segment the way a vector kind is — which is what let the data texture
 *  ship beside the paletted plate it replaced, without invalidating a single
 *  cached URL, and then replace it. */
export function textureUrl(
  modelId: string,
  run: string,
  varId: string,
  width: number,
  lead: number,
): string {
  return `/api/layers/${modelId}/${run}/${varId}/${width}/texture/m${String(lead).padStart(5, '0')}.png`
}

/** The immutable vector-step URL — the plate route's twin.
 *
 *  Under `/api/layers/` deliberately: that prefix is what the service worker's
 *  `CacheFirst` rule matches, and a vector step is as immutable as a frame for
 *  the same reason — the run is in the path. */
export function vectorUrl(
  modelId: string,
  run: string,
  varId: string,
  kind: VectorKind,
  lead: number,
): string {
  return `/api/layers/${modelId}/${run}/${varId}/${kind}/m${String(lead).padStart(5, '0')}.json`
}

/**
 * A lead time in minutes, as a person says it: `+2 h`, `+45 min`, `+2 h 15 m`.
 *
 * One function, because the axis is minutes everywhere now and every place
 * that prints one — the status panel, the map menu — would otherwise reinvent
 * the same three cases, including the sub-hourly one that only HRRR has.
 */
export function leadLabel(minutes: number): string {
  if (minutes === 0) return 'analysis'
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) return `+${rest} min`
  return rest === 0 ? `+${hours} h` : `+${hours} h ${rest} m`
}

/**
 * What the model data is using on disk.
 *
 * Not cached anywhere — it walks a directory tree, which is why the service
 * worker leaves it alone — and a failure is null rather than an exception: the
 * status panel simply omits the line.
 */
export async function fetchStorage(): Promise<StorageReport | null> {
  try {
    const resp = await fetch('/api/storage')
    if (!resp.ok) return null
    return (await resp.json()) as StorageReport
  } catch {
    return null
  }
}

/** Bytes as a person reads them. One place, because the panel and any future
 *  log line would otherwise each invent their own rounding. */
export function byteLabel(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let size = bytes
  let i = 0
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024
    i++
  }
  return i === 0 ? `${Math.round(size)} B` : `${size.toFixed(size < 10 ? 1 : 0)} ${units[i]}`
}

/** Tests only: the module-level catalog cache outlives a component. */
export function _resetModelCache(): void {
  catalog = null
  fetchedAt = 0
}
